import { useMemo, useState } from "react";

import { MATCH_TYPE } from "../utils/const";
import {
  findActiveMatchDOM,
  findAllMatchesDOM,
  makeActiveMatchDefault,
  makeDefaultMatchActive,
  scrollToMatch,
} from "../utils/dom";
import { makeSearchRegExp } from "../utils/searchTerm";
import { calcMatchesMetadata, calcSucceedingMatch } from "../utils/navigation";

/*
  interface TableMatchMeta {
    tableId: string
  }

  interface MatchGroup {
    type: MATCH_TYPE
    count: number
    meta?: TableMatchMeta
  }
  type SectionMatches = MatchGroup[]
  type Matches = SectionMatches[]
*/

export const useMatchNavigation = ({
  openSectionByIndex,
  handleOpenTable,
  handleTelemetry,
  currentSectionIndex,
  inputValue,
}) => {
  const [matches, setMatches] = useState([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);

  const handleMatchNavigation = (direction) => {
    handleTelemetry(direction);

    const { sectionIndex, groupIndex, matchIndex } = calcSucceedingMatch({
      direction,
      matches,
      activeSectionIndex,
      activeMatchIndex,
    });

    setActiveMatchIndex(matchIndex);
    setActiveSectionIndex(sectionIndex);

    if (sectionIndex !== currentSectionIndex) {
      openSectionByIndex(sectionIndex);
      return;
    }

    const activeGroup = matches[sectionIndex][groupIndex] || null;
    if (activeGroup?.type === MATCH_TYPE.TABLE) {
      const searchRegExp = makeSearchRegExp(inputValue);
      handleOpenTable({
        tableId: activeGroup.meta.tableId,
        searchRegExp,
        matches,
        activeSectionIndex: sectionIndex,
        activeGroupIndex: groupIndex,
        activeMatchIndex: matchIndex,
      });
    }

    const activeMatchDOM = findActiveMatchDOM();
    if (!activeMatchDOM) {
      return;
    }

    const allMatchesDOM = findAllMatchesDOM();
    const nextActiveMatchDOM = allMatchesDOM[matchIndex];

    makeActiveMatchDefault(activeMatchDOM);
    makeDefaultMatchActive(nextActiveMatchDOM);

    scrollToMatch(nextActiveMatchDOM);
  };

  const { activeMatchIndexOffset, matchesCount } = useMemo(
    () => calcMatchesMetadata(matches, activeSectionIndex),
    [matches, activeSectionIndex]
  );

  return {
    matches,
    setMatches,
    activeMatchIndex,
    setActiveMatchIndex,
    activeSectionIndex,
    setActiveSectionIndex,
    handleMatchNavigation,
    activeMatchIndexOffset,
    matchesCount,
  };
};
